import { getOrder } from "@/api/orders";
import { IStore, IStoreOrder, getInvoiceItem } from "@/api/store";
import { PaymentIntentNotFound } from "@/components/dashboard";
import { BackButton } from "@/components/ui/BackButton";
import { Backdrop, Box, CircularProgress, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { InvoiceCheckoutForm } from "./InvoiceCheckoutForm";
import { IInvoiceItem } from "@/api/invoice";

interface ICheckoutPageProps {
    store: IStore;
}

export const InvoiceCheckoutPage = ({ store }: ICheckoutPageProps) => {
    const navigate = useNavigate();
    const { order_id } = useParams();

    const { isPending: orderIsPending, data: order } = useQuery<IStoreOrder>({
        queryKey: ["checkout-store-order", order_id],
        queryFn: () => {
            if (order_id) {
                return getOrder(order_id);
            } else throw Error("No invoice item");
        },
        enabled: !!order_id,
    });

    const {
        isPending: invoiceItemIsPending,
        isError: invoiceItemIsError,
        data: invoiceItem,
    } = useQuery<IInvoiceItem>({
        queryKey: ["checkout-invoice-item", order],
        queryFn: () => {
            if (order && order.invoice_item_id) {
                return getInvoiceItem(store.id, order.invoice_item_id);
            } else throw Error("No invoice item");
        },
        enabled: !!order,
    });

    if (invoiceItemIsPending || orderIsPending)
        return (
            <Backdrop
                open={orderIsPending || invoiceItemIsPending}
                sx={{
                    color: "#fff",
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                }}
            >
                <CircularProgress sx={{ color: "#ffffff" }} />
            </Backdrop>
        );

    if (invoiceItemIsError) {
        return <PaymentIntentNotFound />;
    }

    if (!invoiceItem || !order) return null;

    // if (invoiceItem.status === "succeeded" || order.payment_intent_paid) {
    //     return <Navigate to={`/stores/${store.id}/shipping-form`} replace />;
    // }

    return (
        <Box p={"1em"}>
            {/* Payment Information Header */}
            <Typography
                variant="h5"
                textAlign={"center"}
                fontWeight={900}
                color={"#2B3674"}
            >
                Payment Information
            </Typography>
            <Typography
                variant="h6"
                textAlign={"center"}
                fontWeight={600}
                color={"#2B3674"}
                mb={1}
            >
                {store.name}
            </Typography>
            <Typography
                variant="body1"
                textAlign={"center"}
                textTransform={"capitalize"}
                color={"#2B3674"}
                mb={1}
            >
                {[
                    store.store_address.address1,
                    store.store_address.address2,
                    store.store_address.city,
                    store.store_address.state,
                    store.store_address.zip_code,
                ]
                    .filter(Boolean)
                    .join(", ")}
            </Typography>
            <Box maxWidth={"lg"} mx={"auto"} my={"1em"}>
                {/* Back button sends back payment intent state */}
                <BackButton
                    onClick={() =>
                        navigate(
                            `/stores/${store.id}/shipping-form?order_id=${order_id}`
                        )
                    }
                />
                {/* Render Checkout Form */}
                <InvoiceCheckoutForm order={order} invoiceItem={invoiceItem} />
            </Box>
        </Box>
    );
};
